import { ComponentProps } from 'react'

import { pxToRem } from '@/shared/styles/themes'
import { css, IconButton as MuiIconButton, styled, Tooltip } from '@mui/material'

export type IconButtonProps = ComponentProps<typeof StyledIconButton> & {
  round?: boolean
  dark?: boolean // Force dark theme for nav sidebar
  tooltipTitle?: string
  slotProps?: {
    tooltip?: Omit<ComponentProps<typeof Tooltip>, 'title' | 'children'>
  }
}

export const IconButton: React.FC<IconButtonProps> = ({ dark, tooltipTitle, slotProps, round = true, ...props }) => {
  return props.disabled ? (
    <StyledIconButton
      $dark={dark}
      $round={round}
      disableRipple
      {...props}
    />
  ) : (
    <Tooltip title={tooltipTitle || ''} arrow placement="right" {...slotProps?.tooltip}>
      <StyledIconButton
        $dark={dark}
        $round={round}
        disableRipple
        {...props}
      />
    </Tooltip>
  )
}

const StyledIconButton = styled(MuiIconButton, {
  shouldForwardProp: (prop) => prop !== '$dark' && prop !== '$round',
}) <{ $dark?: boolean, $round?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border-radius: ${props => props.$round ? '1000px' : pxToRem(4)};
  padding: 0;
  transition: background-color 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms, width 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  width: ${pxToRem(32)};
  height: ${pxToRem(32)};
  color: ${({ theme, $dark }) => $dark ? theme.palette.custom.sidebar.textDimColor : theme.palette.text.dim};

  &.Mui-disabled {
    .MuiTypography-root {
      color: ${(props) => props.theme.palette.text.disabled};
    }
    
    .MuiSvgIcon-root {
      color: ${(props) => props.theme.palette.text.disabled};
    }
      
    box-shadow: none;
  }

  ${(props) => !props.$round && css`
    &:focus {
        background-color: ${props.$dark ? 'rgba(255, 255, 255, 0.15)' : props.theme.palette.action.focus};
      }

      &:hover {
        color: ${props.$dark ? props.theme.palette.custom.sidebar.color : props.theme.palette.text.primary};
        background-color: ${props.$dark ? 'rgba(255, 255, 255, 0.15)' : props.theme.palette.action.hover};
        border-color: ${props.$dark ? props.theme.palette.text.disabled : 'transparent'};
      }
  `}

  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background-color: ${(props) => props.theme.palette.action.hover};
    opacity: 0;
    transform: scaleX(1) scaleY(1) perspective(1px);
    transition: transform 0.2s cubic-bezier(0.35, 0.12, 0.14, 1.4), opacity 0.1s ease;
  }

  &:hover {
    &:after {
      opacity: 1;
      background-color: ${(props) => props.theme.palette.action.hover};
      transform: scaleX(1) scaleY(1) perspective(1px);
    }
  }

  &:active{
    &:after {
      transform: scaleX(0.95) scaleY(0.95) perspective(1px);
    }
  }

  ${(props) => props.$round && css`
    &:before {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 1000px;
      background-color: ${props.theme.palette.action.hover};
      opacity: 0;
      transform: scaleX(0.8) scaleY(0.8) perspective(1px);
      transition: transform 0.25s cubic-bezier(0.35, 0.12, 0.14, 1.4), opacity 0.25s ease;
    }

    &:after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: 1000px;
      background-color: ${props.theme.palette.action.hover};
      opacity: 0;
      transform: scaleX(0.8) scaleY(0.8) perspective(1px);
      transition: transform 0.2s cubic-bezier(0.35, 0.12, 0.14, 1.4), opacity 0.1s ease;
    }

    &:hover {
      &:after {
        opacity: 1;
        background-color: ${props.theme.palette.action.hover};
        transform: scaleX(1.035) scaleY(1.035) perspective(1px);
      }

      &:before {
        opacity: 1;
        background-color: ${props.theme.palette.action.hover};
        transform: scaleX(1.035) scaleY(1.035) perspective(1px);
      }
    }

    &:active{
      &:after {
        transform: scaleX(0.95) scaleY(0.95) perspective(1px);
      }

      &:before {
        transform: scaleX(0.95) scaleY(0.95) perspective(1px);
      }
    }
  `}
`